import moment from 'moment'

/**
 * Max extended hour/minute for overnight shifts.
 * 26:00 means the shift can end exactly at 02:00 of the next day.
 * At the max hour (26) only minute 0 is allowed — no 26:01–26:59.
 */
export const MAX_EXTENDED_HOUR = 26
export const MAX_EXTENDED_MINUTE = 0
export const MAX_SHIFT_DURATION_HOURS = 26

// Upper bound (minutes from the shift's start-of-day) for extended display: 26:00.
const MAX_EXTENDED_TOTAL_MINUTES = MAX_EXTENDED_HOUR * 60 + MAX_EXTENDED_MINUTE

/**
 * Format time with extended hours (24:00 - 26:00) for overnight shifts.
 *
 * When a shift crosses midnight, instead of showing "01:30" (ambiguous),
 * this shows "25:30" to make it clear it's the next day.
 *
 * @param {string|Date|moment} shiftStartTime - The shift's start time (used to determine the "base day")
 * @param {string|Date|moment} targetTime - The time to format
 * @returns {string} Time as "HH:mm" or "24:mm"/"25:mm" if next day
 *
 * @example
 *   formatExtendedTime('2026-03-15T22:00', '2026-03-16T01:30') // → '25:30'
 *   formatExtendedTime('2026-03-15T09:00', '2026-03-15T17:00') // → '17:00'
 *   formatExtendedTime('2026-03-15T22:00', '2026-03-16T00:00') // → '24:00'
 */
export const formatExtendedTime = (shiftStartTime, targetTime) => {
  const shiftDay = moment(shiftStartTime).startOf('day')
  const target = moment(targetTime)
  const diffMinutes = target.diff(shiftDay, 'minutes')
  const hours = Math.floor(diffMinutes / 60)
  const minutes = diffMinutes % 60

  // Only render extended form for 24:00–26:00. Guard by total minutes (not just
  // the hour) so 26:01–26:59 fall back to normal HH:mm instead of leaking "26:30".
  if (hours >= 24 && diffMinutes <= MAX_EXTENDED_TOTAL_MINUTES) {
    return `${hours}:${String(minutes).padStart(2, '0')}`
  }
  return target.format('HH:mm')
}

/**
 * Parse an extended time string (e.g. "25:30") into a moment object
 * based on a given base date.
 *
 * @param {string|Date|moment} baseDate - The base date of the shift
 * @param {string} extendedTimeStr - Time as "HH:mm" or "25:30"
 * @returns {moment.Moment} Moment object with the correct date
 *
 * @example
 *   parseExtendedTime('2026-03-15', '25:30') // → moment('2026-03-16T01:30:00')
 *   parseExtendedTime('2026-03-15', '09:00') // → moment('2026-03-15T09:00:00')
 */
export const parseExtendedTime = (baseDate, extendedTimeStr) => {
  const [hours, minutes] = extendedTimeStr.split(':').map(Number)
  const base = moment(baseDate).startOf('day')

  if (hours >= 24) {
    return base
      .clone()
      .add(1, 'day')
      .hour(hours - 24)
      .minute(minutes)
      .second(0)
  }
  return base.clone().hour(hours).minute(minutes).second(0)
}

/**
 * Check if a targetTime falls on the next day compared to the shift's start time.
 *
 * @param {string|Date|moment} shiftStartTime
 * @param {string|Date|moment} targetTime
 * @returns {boolean}
 */
export const isNextDayTime = (shiftStartTime, targetTime) => {
  const shiftDay = moment(shiftStartTime).startOf('day')
  const targetDay = moment(targetTime).startOf('day')
  return targetDay.isAfter(shiftDay)
}

/**
 * Get the total duration of a shift in hours (can be > 24).
 *
 * @param {string|Date|moment} startTime
 * @param {string|Date|moment} endTime
 * @returns {number}
 */
export const getShiftDurationHours = (startTime, endTime) => {
  return moment(endTime).diff(moment(startTime), 'hours', true)
}

/**
 * Convert a Date/moment that may be on the next day back to an extended time string.
 * Used when loading data from API back into the form (reverse logic).
 *
 * @param {string|Date|moment} shiftStartTime - The shift's start time (base day reference)
 * @param {string|Date|moment} targetTime - The time to convert
 * @returns {string} Extended time string like "25:30" or normal "17:00"
 */
export const toExtendedTimeString = (shiftStartTime, targetTime) => {
  return formatExtendedTime(shiftStartTime, targetTime)
}
